'use client';
import { usePostHogEvents } from '@/hooks/usePostHogEvents';
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import { generateDirectusSrc } from '@/helpers/image';

export function getYouTubeId(url: string): string | null {
  if (!url) return null;
  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
}

const VideoComponent = ({
  file,
  mobile_file,
  thumbnail,
  loop,
  autoplay,
  youtube_url,
  sound,
  restart,
  mux_playback_id,
  mobile_mux_playback_id,
  preload,
  eager = false,
  sizes,
}: {
  file: string;
  mobile_file?: string | null;
  thumbnail?: string | null;
  loop?: boolean;
  autoplay?: boolean;
  youtube_url?: string | null;
  sound?: boolean;
  restart?: number;
  mux_playback_id?: string | null;
  mobile_mux_playback_id?: string | null;
  preload?: 'auto' | 'metadata' | 'none';
  eager?: boolean;
  sizes?: string;
}) => {
  const resolvedSizes = sizes ?? '(max-width: 768px) 100vw, 1200px';
  const resolvedPreload = preload ?? (autoplay ? 'auto' : 'metadata');
  const youtubeId = youtube_url ? getYouTubeId(youtube_url) : null;
  const customThumbnailUrl = thumbnail
    ? generateDirectusSrc(thumbnail, 1200)
    : undefined;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [hasPlayed, setHasPlayed] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [videoEnded, setVideoEnded] = useState<boolean>(false);

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '200px 0px 200px 0px',
  });

  const [hasBeenNearViewport, setHasBeenNearViewport] =
    useState<boolean>(!!eager);

  const { ref: nearRef } = useInView({
    rootMargin: '1000px 0px 1000px 0px', // Trigger load when within 1000px of viewport
    triggerOnce: true,
    skip: !!eager,
    onChange: (inView) => {
      if (inView) {
        setHasBeenNearViewport(true);
      }
    },
  });

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      ref(node);
      nearRef(node);
    },
    [ref, nearRef]
  );

  const resolvedPlaybackId = isMobile
    ? mobile_mux_playback_id ?? mux_playback_id
    : mux_playback_id;
  const { sendVideoPlayEvent } = usePostHogEvents();

  const [MuxVideoComp, setMuxVideoComp] =
    useState<React.ComponentType<any> | null>(null);
  const [playRequested, setPlayRequested] = useState<boolean>(false);
  const [loadVideo, setLoadVideo] = useState<boolean>(!!autoplay || !!eager);

  useEffect(() => {
    if (autoplay || eager) {
      setLoadVideo(true);
    }
  }, [autoplay, eager]);

  useEffect(() => {
    if (hasBeenNearViewport && loadVideo) {
      import('@mux/mux-video-react')
        .then((mod) => {
          setMuxVideoComp(() => mod.default);
        })
        .catch((err) => {
          console.error('Failed to load MuxVideo component:', err);
        });
    }
  }, [hasBeenNearViewport, loadVideo]);

  useEffect(() => {
    if (!inView) {
      setPlayRequested(false);
    }
  }, [inView]);

  useEffect(() => {
    if (playRequested && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
      sendVideoPlayEvent(file);
      setVideoEnded(false);
      setPlayRequested(false);
    }
  }, [playRequested, MuxVideoComp, file, sendVideoPlayEvent]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setHasPlayed(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleVideoEnd = useCallback(() => {
    setIsPlaying(false);
    setVideoEnded(true);
    if (thumbnail) {
      setHasPlayed(false); // Reset to show thumbnail at the end of the video
    }
  }, [thumbnail]);

  const handleThumbnailClick = useCallback(() => {
    setHasBeenNearViewport(true);
    setLoadVideo(true);
    setPlayRequested(true);
  }, []);

  const handlePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        if (videoEnded || videoRef.current.ended) {
          videoRef.current.currentTime = 0;
        }
        videoRef.current.play().catch(() => {});
        sendVideoPlayEvent(file);
        setVideoEnded(false);
      }
    } else {
      setHasBeenNearViewport(true);
      setLoadVideo(true);
      setPlayRequested(true);
    }
  }, [isPlaying, file, sendVideoPlayEvent, videoEnded]);

  const handleMuteUnmute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile(); // Run initially
    window.addEventListener('resize', checkMobile); // Update on resize

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) return;

    if (autoplay) {
      if (inView) {
        video.play().catch(() => {});
        setVideoEnded(false);
      } else {
        video.pause();
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
        }
        setHasPlayed(false); // Reset when paused/reset due to going out of view
      }
    } else {
      if (!inView) {
        video.pause();
      }
    }
  }, [autoplay, inView, hasBeenNearViewport]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
    setIsPlaying(false);
    setVideoEnded(false);
    setHasPlayed(false); // Reset when restarted
  }, [restart]);

  const showThumbnail =
    !hasBeenNearViewport ||
    !loadVideo ||
    (resolvedPlaybackId ? !MuxVideoComp : false) ||
    (!hasPlayed && (!videoEnded || !!thumbnail)) ||
    (videoEnded && !loop && !!thumbnail);

  return (
    <div
      ref={setRefs}
      className="w-full h-full relative rounded-[10px] overflow-hidden isolate"
    >
      {youtube_url && youtubeId ? (
        <LiteYouTubeEmbed
          id={youtubeId}
          title="YouTube Video"
          thumbnail={customThumbnailUrl}
          lazyLoad={true}
          webp={true}
          poster="maxresdefault"
        />
      ) : youtube_url ? (
        <div className="relative w-full h-full bg-[#111] flex items-center justify-center text-white text-sm">
          Invalid YouTube URL
        </div>
      ) : (
        <>
          {showThumbnail && (
            <div
              className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black rounded-[10px] overflow-hidden z-[2]"
              onClick={handleThumbnailClick}
            >
              {thumbnail ? (
                <Image
                  src={generateDirectusSrc(thumbnail, 1200)}
                  alt="Video thumbnail"
                  fill
                  sizes={resolvedSizes}
                  quality={80}
                  priority={eager}
                  className="object-cover rounded-[10px]"
                />
              ) : resolvedPlaybackId ? (
                <Image
                  src={`https://image.mux.com/${resolvedPlaybackId}/thumbnail.webp?time=0&width=1200`}
                  alt="Video thumbnail"
                  fill
                  sizes={resolvedSizes}
                  quality={80}
                  priority={eager}
                  className="object-cover rounded-[10px]"
                />
              ) : (
                <div className="w-full h-full bg-[#111] rounded-[10px]" />
              )}
              {/* Play button overlay on thumbnail */}
              {!autoplay && (
                <div
                  onClick={handlePlayPause}
                  className="w-[45px] h-[45px] absolute bottom-3 right-3 rounded-full bg-[#24272c] flex flex-shrink-0 items-center justify-center cursor-pointer z-[3]"
                >
                  <Image
                    alt="play"
                    src="/assets/video/play.svg"
                    width={20}
                    height={20}
                    className="invert min-h-[20px] max-h-[20px] min-w-[20px] max-w-[20px] shrink-0 h-[20px] w-[20px]"
                  />
                </div>
              )}
            </div>
          )}
          {hasBeenNearViewport && loadVideo && (
            <>
              {resolvedPlaybackId ? (
                MuxVideoComp ? (
                  <MuxVideoComp
                    ref={videoRef}
                    playbackId={resolvedPlaybackId}
                    preload={resolvedPreload}
                    autoPlay={autoplay ? true : false}
                    muted={isMuted}
                    playsInline={true}
                    loop={loop}
                    maxResolution="720p"
                    className="w-full h-full object-cover rounded-[10px] overflow-hidden"
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                    poster={
                      thumbnail
                        ? generateDirectusSrc(thumbnail, 1200)
                        : `https://image.mux.com/${resolvedPlaybackId}/thumbnail.webp?time=0&width=1200`
                    }
                    onPlay={handlePlay}
                    onPlaying={handlePlay}
                    onPause={handlePause}
                    onEnded={handleVideoEnd}
                  />
                ) : (
                  <div className="w-full h-full bg-[#111] rounded-[10px]" />
                )
              ) : (
                <video
                  key={isMobile ? 'mobile' : 'desktop'} // Force re-render
                  ref={videoRef}
                  className="w-full h-full object-cover rounded-[10px] overflow-hidden"
                  disablePictureInPicture={true}
                  disableRemotePlayback={true}
                  playsInline
                  loop={loop}
                  muted={isMuted}
                  controls={false}
                  preload={resolvedPreload}
                  poster={
                    thumbnail ? generateDirectusSrc(thumbnail, 1200) : undefined
                  }
                  onPlay={handlePlay}
                  onPlaying={handlePlay}
                  onPause={handlePause}
                  onEnded={handleVideoEnd}
                >
                  <source
                    src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${isMobile ? mobile_file ?? file : file}`}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              )}
            </>
          )}

          {hasBeenNearViewport && loadVideo && (
            <div className="absolute flex gap-2 bottom-3 z-10 right-3">
              {sound && (
                <div
                  onClick={handleMuteUnmute}
                  className="w-[45px] h-[45px] rounded-full bg-[#24272c] flex flex-shrink-0 items-center justify-center cursor-pointer"
                >
                  <Image
                    alt={isMuted ? 'unmute' : 'mute'}
                    src={
                      isMuted
                        ? '/assets/video/mute.svg'
                        : '/assets/video/sound.svg'
                    }
                    width={20}
                    height={20}
                    className="invert min-h-[20px] max-h-[20px] min-w-[20px] shrink-0 max-w-[20px] h-[20px] w-[20px]"
                  />
                </div>
              )}

              <div
                onClick={handlePlayPause}
                className="w-[45px] h-[45px] rounded-full bg-[#24272c] flex flex-shrink-0 items-center justify-center cursor-pointer"
              >
                <Image
                  alt={isPlaying ? 'pause' : 'play'}
                  src={
                    isPlaying
                      ? '/assets/video/pause.svg'
                      : '/assets/video/play.svg'
                  }
                  width={20}
                  height={20}
                  className="invert min-h-[20px] max-h-[20px] min-w-[20px] max-w-[20px] shrink-0 h-[20px] w-[20px]"
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VideoComponent;
